import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Package, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const GlobalSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState({ clients: [], equipment: [] });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function
  useEffect(() => {
    const searchData = async () => {
      if (!searchQuery.trim()) {
        setResults({ clients: [], equipment: [] });
        setShowResults(false);
        return;
      }

      setLoading(true);
      setShowResults(true);

      try {
        const query = searchQuery.toLowerCase();
        
        // Search clients (admin only)
        let clients = [];
        if (user?.role === 'admin') {
          const allClients = await apiService.getClients();
          clients = allClients
            .filter(client => 
              client.role === 'client' &&
              (client.name?.toLowerCase().includes(query) ||
               client.email?.toLowerCase().includes(query) ||
               client.company?.toLowerCase().includes(query))
            )
            .slice(0, 5); // Limit to 5 results
        }

        // Search equipment
        const allEquipment = await apiService.getEquipment(
          user?.role === 'client' ? user.id : undefined
        );
        const equipment = allEquipment
          .filter(item =>
            item.serialNumber?.toLowerCase().includes(query) ||
            item.type?.toLowerCase().includes(query) ||
            item.location?.toLowerCase().includes(query)
          )
          .slice(0, 5); // Limit to 5 results

        setResults({ clients, equipment });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchData, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, user]);

  const handleClientClick = (client) => {
    navigate(`/clients/${client.id}/sites`);
    setSearchQuery('');
    setShowResults(false);
  };

  const handleEquipmentClick = (equipment) => {
    if (user?.role === 'admin') {
      navigate(`/equipment/edit/${equipment.id}`);
    } else {
      navigate(`/my-equipment/${equipment.id}`);
    }
    setSearchQuery('');
    setShowResults(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setResults({ clients: [], equipment: [] });
    setShowResults(false);
  };

  const hasResults = results.clients.length > 0 || results.equipment.length > 0;

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher un client ou équipement..."
          className="w-full pl-10 pr-10 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-white transition-all"
        />
        {searchQuery && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full mt-2 w-full bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50 max-h-96 overflow-y-auto"
          >
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500 mx-auto"></div>
              </div>
            ) : hasResults ? (
              <div className="py-2">
                {/* Clients Section */}
                {results.clients.length > 0 && (
                  <div>
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Clients
                    </div>
                    {results.clients.map((client) => (
                      <motion.button
                        key={client.id}
                        whileHover={{ backgroundColor: 'rgba(239, 68, 68, 0.05)' }}
                        onClick={() => handleClientClick(client)}
                        className="w-full px-4 py-3 flex items-start space-x-3 text-left transition-colors"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {client.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {client.email}
                          </p>
                          {client.company && (
                            <p className="text-xs text-gray-400 truncate">
                              {client.company}
                            </p>
                          )}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Equipment Section */}
                {results.equipment.length > 0 && (
                  <div className={results.clients.length > 0 ? 'mt-2 pt-2 border-t border-gray-100' : ''}>
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Équipements
                    </div>
                    {results.equipment.map((equipment) => (
                      <motion.button
                        key={equipment.id}
                        whileHover={{ backgroundColor: 'rgba(239, 68, 68, 0.05)' }}
                        onClick={() => handleEquipmentClick(equipment)}
                        className="w-full px-4 py-3 flex items-start space-x-3 text-left transition-colors"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-md">
                          <Package className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {equipment.serialNumber}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {equipment.type}
                          </p>
                          {equipment.location && (
                            <p className="text-xs text-gray-400 truncate">
                              📍 {equipment.location}
                            </p>
                          )}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500 text-sm">
                Aucun résultat trouvé
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalSearch;






